import { useEffect, useState, useMemo, useRef } from "react";
import { InfoWindow } from "@vis.gl/react-google-maps";
import MarkerEditor from "./MarkerEditor";
import { MarkerViewer } from "./MarkerComponents";
import { useMarkerMediaActions } from "../../../media/hooks/useMarkerMediaActions";
import { prepareFileListForMedia } from "../../../../utils/prepareImageFileForUpload";


const DESKTOP_INFO_WINDOW_WIDTH = 800;

const viewerInfoWindowOptions = {
    pixelOffset: { x: 0, y: -30 },
    maxWidth: DESKTOP_INFO_WINDOW_WIDTH,
    minWidth: DESKTOP_INFO_WINDOW_WIDTH,
    disableAutoPan: false,
    shouldFocusOnLoad: false,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 12,
    boxStyle: {
        background: 'transparent',
        border: 'none',
        borderRadius: '12px',
        boxShadow: 'none',
        width: `${DESKTOP_INFO_WINDOW_WIDTH}px`,
        maxWidth: `${DESKTOP_INFO_WINDOW_WIDTH}px`,
        overflow: 'hidden',
    },
};

// タブレット・スマホ用のInfoWindowオプション（高さは googleMapsInfoWindow.css と一致）
const getMobileInfoWindowOptions = () => {
    const isTablet = window.innerWidth <= 768;
    const isMobile = window.innerWidth <= 440;

    if (isTablet) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // CSS: ≤768 → 90vw / 50vh; ≤440 → 98vw / 85vh
        let widthRatio = 0.90;
        let heightRatio = 0.50;

        if (isMobile) {
            widthRatio = 0.98;
            heightRatio = 0.85;
        }

        const maxWidth = Math.floor(viewportWidth * widthRatio);
        const maxHeight = Math.floor(viewportHeight * heightRatio);

        return {
            pixelOffset: { x: 0, y: -30 },
            maxWidth: maxWidth,
            minWidth: maxWidth,
            maxHeight: maxHeight,
            minHeight: maxHeight,
            disableAutoPan: false,
            shouldFocusOnLoad: false,
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: 8,
            boxStyle: {
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                boxShadow: 'none',
                width: `${maxWidth}px`,
                maxWidth: `${maxWidth}px`,
                height: `${maxHeight}px`,
                overflow: 'hidden',
            }
        };
    }
    return viewerInfoWindowOptions;
};

export function MarkerEditorContainer({
    marker,
    onChange,
    onSavePin,
    onClose,
    onDelete,
    labelOptions,
    tmpMediaList,
    addMedia,
    uploadMedia,
    deleteMedia,
    removeFileAt,
    setIsUploadingMedia,
    setUploadProgress
}) {
    const [localMediaList, setLocalMediaList] = useState([]);
    const [pendingDeletions, setPendingDeletions] = useState([]);       // 削除保留: 既存メディアのID[]
    const [pendingNewFiles, setPendingNewFiles] = useState([]);          // 追加保留: 未アップロードのファイル[]
    const [pendingMetaUpdates, setPendingMetaUpdates] = useState(new Map()); // メタ更新保留: Map<id, {name, description}>
    const { fetchList: localFetchList, deleteOne: localDeleteOne, uploadAll: localUploadAll, updateMediaMeta: localUpdateMediaMeta } = useMarkerMediaActions(setLocalMediaList);
    const localFetchListRef = useRef(localFetchList);
    const localDeleteOneRef = useRef(localDeleteOne);
    const localUploadAllRef = useRef(localUploadAll);
    const localUpdateMediaMetaRef = useRef(localUpdateMediaMeta);

    // localFetchList、localDeleteOne、localUploadAll、localUpdateMediaMetaの参照を更新
    useEffect(() => {
        localFetchListRef.current = localFetchList;
        localDeleteOneRef.current = localDeleteOne;
        localUploadAllRef.current = localUploadAll;
        localUpdateMediaMetaRef.current = localUpdateMediaMeta;
    }, [localFetchList, localDeleteOne, localUploadAll, localUpdateMediaMeta]);

    // 表示用メディアリストの計算ロジック
    const calculateDisplayMediaList = () => {
        const existingMedia = localMediaList
            .filter(media => !pendingDeletions.includes(media.id))
            .map(media => {
                const update = pendingMetaUpdates.get(media.id);
                return update ? { ...media, ...update } : media;
            });
        return [...existingMedia, ...pendingNewFiles];
    };

    // 新規pin（tmpPin）の場合はtmpMediaListを使用、既存pinの場合はローカル状態を使用
    const displayMediaList = marker.id ? calculateDisplayMediaList() : tmpMediaList;

    // 既存pinの場合のローカルメディア操作関数
    const localAddFiles = async (fileList) => {
        const newFiles = await prepareFileListForMedia(fileList);
        setPendingNewFiles((prev) => {
            const filtered = newFiles.filter(
                (file) =>
                    !prev.some(
                        (p) =>
                            p?.file &&
                            p.file.name === file.name &&
                            p.file.size === file.size &&
                            p.file.type === file.type
                    ) &&
                    !localMediaList.some(
                        (p) =>
                            p?.file &&
                            p.file.name === file.name &&
                            p.file.size === file.size &&
                            p.file.type === file.type
                    )
            );
            const wrapped = filtered.map((file) => ({
                id: Date.now().toString(36) + Math.random().toString(36).substr(2),
                file,
                name: '',
                description: '',
                media_type: file.type,
                mediaType: file.type.startsWith('image/') ? 'image' :
                    file.type.startsWith('video/') ? 'video' :
                        file.type.startsWith('audio/') ? 'audio' : 'image',
                url: null,
            }));
            return [...prev, ...wrapped];
        });
    };

    const localRemoveFileAt = (index) => {
        const currentDisplayList = calculateDisplayMediaList();
        const itemToRemove = currentDisplayList[index];
        if (!itemToRemove) return;

        if (itemToRemove.file && !itemToRemove.url) {
            // 未アップロードの新規ファイル
            setPendingNewFiles(prev => prev.filter(item => item.id !== itemToRemove.id));
        } else {
            // 既存メディアを削除保留リストに追加（重複ガード）
            setPendingDeletions(prev => prev.includes(itemToRemove.id) ? prev : [...prev, itemToRemove.id]);
        }
    };

    const localUpdateFileMeta = async (index, key, value) => {
        const currentDisplayList = calculateDisplayMediaList();
        const itemToUpdate = currentDisplayList[index];
        if (!itemToUpdate) return;

        if (itemToUpdate.file && !itemToUpdate.url) {
            // 未アップロードの新規ファイルのメタ更新
            setPendingNewFiles(prev =>
                prev.map(item =>
                    item.id === itemToUpdate.id ? { ...item, [key]: value } : item
                )
            );
        } else {
            // 既存メディアのメタ更新を Map に蓄積
            setPendingMetaUpdates(prev => {
                const next = new Map(prev);
                const existing = next.get(itemToUpdate.id) || {};
                next.set(itemToUpdate.id, { ...existing, [key]: value });
                return next;
            });
        }
    };

    // メディアのバリデーションをチェック
    const validateMedia = () => {
        // displayMediaListと同じロジックを使用して重複を避ける
        const currentDisplayList = calculateDisplayMediaList();

        const invalidItems = currentDisplayList.filter(item =>
            !item.name || item.name.trim() === ''
        );

        if (invalidItems.length > 0) {
            // アラートは表示せず、コンソールログのみ
            console.warn(`${invalidItems.length}個のメディアでタイトルが入力されていません。`);
            return false;
        }
        return true;
    };

    // 既存pinの場合の保存処理をラップ
    const handleSavePin = async () => {
        if (marker.id) {
            // バリデーションをチェック
            if (!validateMedia()) {
                return; // バリデーションエラーの場合は保存を中止
            }

            // ローディングUIを即座に表示
            setIsUploadingMedia?.(true);
            setUploadProgress?.({ totalFiles: 0, completedFiles: 0 });

            try {
                // 保留中の削除を実行
                for (const mediaId of pendingDeletions) {
                    await localDeleteOneRef.current(mediaId, marker.id);
                }

                // 新規ファイルのアップロード
                if (pendingNewFiles.length > 0) {
                    try {
                        await localUploadAllRef.current(pendingNewFiles, marker.id, (progress) => {
                            setUploadProgress?.(progress);
                        });
                    } catch (uploadError) {
                        await localFetchListRef.current(marker.id);
                    }
                }

                // 既存メディアのメタ更新
                for (const [mediaId, meta] of pendingMetaUpdates) {
                    try {
                        await localUpdateMediaMetaRef.current(mediaId, {
                            name: meta.name,
                            description: meta.description
                        });
                    } catch (error) {
                        console.error(`Failed to update media ${mediaId}:`, error);
                        throw error;
                    }
                }

                // 保留状態をクリア
                setPendingDeletions([]);
                setPendingNewFiles([]);
                setPendingMetaUpdates(new Map());

                // メディアリストを再読み込み
                await localFetchListRef.current(marker.id);
            } catch (error) {
                console.error("Error in handleSavePin:", error);
                // uploadAllやupdateMediaMeta内で既にtoastを表示しているため、エラーを再throwしない
                // メディアリストを再読み込みして状態を同期
                try {
                    await localFetchListRef.current(marker.id);
                } catch (refreshError) {
                    console.warn('Failed to refresh media list after error:', refreshError);
                }
            } finally {
                setIsUploadingMedia?.(false);
                setUploadProgress?.(null);
            }
        }
        // 元のonSavePinを呼び出し
        // onSavePin内でsetIsUploadingMediaが管理されるため、ここでは設定しない
        onSavePin();
    };

    // marker.id の有無に応じてメディア状態を初期化
    useEffect(() => {
        setPendingDeletions([]);
        setPendingNewFiles([]);
        setPendingMetaUpdates(new Map());

        if (marker.id) {
            const loadMedia = async () => {
                try {
                    await localFetchListRef.current(marker.id);
                } catch (error) {
                    // fetchList 内でエラー処理済み
                }
            };
            loadMedia();
        } else {
            setLocalMediaList([]);
        }
    }, [marker.id]);

    return (
        <InfoWindow position={marker.position} options={getMobileInfoWindowOptions()} onClose={onClose}>
            <MarkerEditor
                marker={marker}
                labelOptions={labelOptions}
                onChange={onChange}
                onSavePin={marker.id ? handleSavePin : onSavePin}
                onDelete={onDelete}
                onClose={onClose}
                tmpMediaList={displayMediaList}
                addMedia={marker.id ? localAddFiles : addMedia}
                uploadMedia={marker.id ? localUpdateFileMeta : uploadMedia}
                deleteMedia={marker.id ? (mediaId, markerId, isNewFile = false, fileIndex = null) => {
                    if (isNewFile && fileIndex !== null) {
                        // 新規ファイル（まだアップロードされていない）の場合
                        localRemoveFileAt(fileIndex);
                    } else {
                        // 既存メディア（データベースに保存済み）の場合
                        // 即座に削除せず、保留削除リストに追加（重複ガード）
                        setPendingDeletions(prev => prev.includes(mediaId) ? prev : [...prev, mediaId]);
                    }
                } : deleteMedia}
                removeFileAt={marker.id ? localRemoveFileAt : removeFileAt}
            />
        </InfoWindow>
    );
}

export function MarkerViewerContainer({ marker, labelOptions, onClose, onEdit, canEdit, initialLoading = false, publicUrl = null }) {
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(initialLoading);
    const [error, setError] = useState(null);

    // useMarkerMediaActionsを使用してメディアリストを管理
    const { fetchList } = useMarkerMediaActions(setMediaList);
    const fetchListRef = useRef(fetchList);

    // fetchListの参照を更新
    useEffect(() => {
        fetchListRef.current = fetchList;
    }, [fetchList]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        const loadMediaList = async () => {
            try {
                // カスタムのsetMediaListを渡して、ソート済みのリストを設定
                const customSetMediaList = (data) => {
                    if (mounted) {
                        const list = Array.isArray(data) ? data : [];
                        const sortedList = [...list].sort((a, b) => {
                            const dateA = new Date(a.uploaded_at);
                            const dateB = new Date(b.uploaded_at);
                            if (dateA.getTime() === dateB.getTime()) {
                                return a.id - b.id;
                            }
                            return dateA.getTime() - dateB.getTime();
                        });
                        setMediaList(sortedList);
                        setLoading(false);
                    }
                };

                await fetchListRef.current(marker.id, customSetMediaList, { publicUrl });
            } catch (err) {
                if (mounted) {
                    setError(err);
                    setLoading(false);
                }
            }
        };

        // グローバルイベントリスナーを追加
        const handleMediaListUpdate = async (event) => {
            if (event.detail.markerId === marker.id) {
                await loadMediaList();
            }
        };

        loadMediaList();
        window.addEventListener('mediaListUpdated', handleMediaListUpdate);

        return () => {
            mounted = false;
            window.removeEventListener('mediaListUpdated', handleMediaListUpdate);
        };
    }, [marker.id, publicUrl]);

    const label = useMemo(
        () => labelOptions.find((l) => l.id === marker.labelId),
        [labelOptions, marker.labelId]
    );

    return (
        <InfoWindow
            key={`viewer-${marker.id}`}
            position={marker.position}
            options={getMobileInfoWindowOptions()}
            onClose={onClose}
        >
            <MarkerViewer
                marker={marker}
                label={label}
                mediaList={mediaList}
                loading={loading}
                error={error}
                onEdit={onEdit}
                canEdit={canEdit}
                onClose={onClose}
            />
        </InfoWindow>
    );
}
